import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa6';
import { useAppContext } from '../../../../shared/context/AppContext';
import { ONBOARDING_QUESTIONS } from '../../domain/models/OnboardingQuestion';
import { submitAnswers } from '../controllers/onboardingQuestionsController';
import QuestionCard from '../components/QuestionCard';
import ProgressBar from '../components/ProgressBar';
import Button from '../../../../shared/components/Button';
import './OnboardingQuestionsScreen.css';

const OnboardingQuestionsScreen = () => {
  const navigate = useNavigate();
  const { setUser, user } = useAppContext();

  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [transitioning, setTransitioning] = useState(false);
  const [direction, setDirection] = useState('forward'); // 'forward' | 'back'
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const question = ONBOARDING_QUESTIONS[currentStep];
  const totalSteps = ONBOARDING_QUESTIONS.length;
  const currentAnswer = answers[question.key] ?? (question.multiSelect ? [] : null);

  const hasSelection = question.multiSelect
    ? Array.isArray(currentAnswer) && currentAnswer.length > 0
    : currentAnswer !== null && currentAnswer !== undefined && currentAnswer !== '';

  const isLastStep = currentStep === totalSteps - 1;

  const handleSelect = (value) => {
    setAnswers((prev) => ({ ...prev, [question.key]: value }));
    setError('');
  };

  const goToStep = (step, dir) => {
    setDirection(dir);
    setTransitioning(true);
    setTimeout(() => {
      setCurrentStep(step);
      setTransitioning(false);
    }, 220);
  };

  const handleContinue = async () => {
    if (!hasSelection) return;

    if (!isLastStep) {
      goToStep(currentStep + 1, 'forward');
      return;
    }

    // Final step — submit
    setSubmitting(true);
    setError('');
    try {
      await submitAnswers(answers);
      // Role assignment happens once at account creation (see ensureUserDocs
      // in AppContext), not here — this step just records the survey answers.
      const updatedUser = { ...user, onboardingCompleted: true };
      localStorage.setItem('user_data', JSON.stringify(updatedUser));

      if (setUser) {
        setUser(updatedUser);
      }
      navigate('/home', { replace: true });
    } catch (err) {
      console.error('Onboarding submission error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (currentStep === 0) return;
    goToStep(currentStep - 1, 'back');
  };

  return (
    <div className="onboarding-questions-screen">
      <div className="onboarding-questions-card">
        <div className="onboarding-questions-gradient-header" />
        <div className="onboarding-questions-card-content">
          {/* Progress bar */}
          <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />

          {/* Question card with transition */}
          <div
            className={`onboarding-question-wrapper ${transitioning ? `transitioning-${direction}` : 'visible'}`}
          >
            <QuestionCard
              question={question}
              selectedValue={currentAnswer}
              onSelect={handleSelect}
            />
          </div>

          {/* Error message */}
          {error && <p className="onboarding-error">{error}</p>}

          {/* Navigation */}
          <div className="onboarding-nav">
            {currentStep > 0 && (
              <button
                type="button"
                className="onboarding-back-btn"
                onClick={handleBack}
                disabled={transitioning || submitting}
                aria-label="Go back"
              >
                <FaArrowLeft className="onboarding-back-icon" />
                Back
              </button>
            )}

            <div className="onboarding-nav-right">
              <Button
                variant="primary"
                className="onboarding-continue-btn"
                onClick={handleContinue}
                disabled={!hasSelection || transitioning || submitting}
              >
                {submitting ? (
                  'Saving...'
                ) : isLastStep ? (
                  "Let's go"
                ) : (
                  <>
                    Continue
                    <FaArrowRight className="onboarding-continue-icon" />
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Step counter */}
          <p className="onboarding-step-counter">
            {currentStep + 1} of {totalSteps}
          </p>
        </div>
      </div>
    </div>
  );
};

export default OnboardingQuestionsScreen;
